import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import type { ApprovalStatus } from "../types";
import type { AgentTokenCreateBody, ApprovalDecisionBody } from "./types";

export function useAgentTokens(workspace: string) {
  return useQuery({
    queryKey: qk.agentTokens(workspace),
    queryFn: () =>
      unwrap(api.GET("/v1/workspaces/{workspace}/agent-tokens", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

/** The plaintext token is in the response exactly once. Show it, then forget it. */
export function useCreateAgentToken(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AgentTokenCreateBody) =>
      unwrap(api.POST("/v1/workspaces/{workspace}/agent-tokens", { params: { path: { workspace }, header: idem() }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.agentTokens(workspace) }),
  });
}

export function useRevokeAgentToken(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      unwrap(
        api.DELETE("/v1/workspaces/{workspace}/agent-tokens/{token_id}", {
          params: { path: { workspace, token_id: tokenId }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.agentTokens(workspace) }),
  });
}

export function useApprovals(workspace: string, filter: { status?: ApprovalStatus; project?: string } = {}) {
  return useQuery({
    queryKey: qk.approvals(workspace, filter),
    queryFn: () =>
      unwrap(api.GET("/v1/workspaces/{workspace}/approvals", { params: { path: { workspace }, query: filter } })),
    refetchInterval: 30_000,
    enabled: !!workspace,
  });
}

export function useApproval(approvalId: string) {
  return useQuery({
    queryKey: qk.approval(approvalId),
    queryFn: () => unwrap(api.GET("/v1/approvals/{approval_id}", { params: { path: { approval_id: approvalId } } })),
    enabled: !!approvalId,
  });
}

/** Approving needs a passkey step-up assertion (POST /v1/auth/step-up/begin, then navigator.credentials.get). */
export function useApproveApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ approvalId, body }: { approvalId: string; body: ApprovalDecisionBody }) =>
      unwrap(
        api.POST("/v1/approvals/{approval_id}/approve", { params: { path: { approval_id: approvalId }, header: idem() }, body }),
      ),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: qk.approval(v.approvalId) });
      void qc.invalidateQueries({ queryKey: ["workspace"] });
    },
  });
}

export function useDenyApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ approvalId, note }: { approvalId: string; note?: string }) =>
      unwrap(
        api.POST("/v1/approvals/{approval_id}/deny", {
          params: { path: { approval_id: approvalId }, header: idem() },
          body: note ? { note } : {},
        }),
      ),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: qk.approval(v.approvalId) });
      void qc.invalidateQueries({ queryKey: ["workspace"] });
    },
  });
}

export function useActivity(
  workspace: string,
  filter: { project?: string; actor_type?: "user" | "agent" | "system" | "ai_ops" | "github"; agent_token?: string } = {},
) {
  return useQuery({
    queryKey: qk.activity(workspace, filter),
    queryFn: () =>
      unwrap(api.GET("/v1/workspaces/{workspace}/activity", { params: { path: { workspace }, query: filter } })),
    enabled: !!workspace,
  });
}

export function useAuditEvents(workspace: string) {
  return useQuery({
    queryKey: qk.auditEvents(workspace),
    queryFn: () =>
      unwrap(api.GET("/v1/workspaces/{workspace}/audit-events", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}
